/**
 * Breadcrumb Sanitizer Utility
 * 
 * Acts as the security bouncer for the Breadcrumb System. Every time a breadcrumb is added 
 * with extra `metadata`, this file cleans it to protect the application from memory leaks 
 * and prevent exposing sensitive Personally Identifiable Information (PII) to telemetry.
 * 
 * It recursively strips out keys like passwords, tokens, and cookies, and truncates 
 * the object if it is too deep or has too many keys. It also strips PII from URLs.
 */
const SENSITIVE_KEY_REGEX = /password|token|secret|auth|cookie|ssn|creditcard/i;
const MAX_KEYS = 5;
const MAX_DEPTH = 2;
const MAX_STRING_LENGTH = 250;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Sanitizes metadata objects to remove PII and enforce structural limits (preventing massive objects).
 */
export function sanitizeMetadata(
    metadata: Record<string, unknown> | undefined,
    currentDepth = 1
): Record<string, unknown> | undefined {
    if (!metadata || !isPlainObject(metadata)) {
        return undefined;
    }

    if (currentDepth > MAX_DEPTH) {
        return { _truncated: 'Max depth exceeded' };
    }

    const result: Record<string, unknown> = {};
    const keys = Object.keys(metadata);
    const keysToProcess = keys.slice(0, MAX_KEYS);
  
    if (keys.length > MAX_KEYS) {
        result['_truncated'] = 'Max keys exceeded';
    }

    for (const key of keysToProcess) {
        if (SENSITIVE_KEY_REGEX.test(key)) {
            result[key] = '[REDACTED]';
            continue;
        }

        const value = metadata[key];

        if (typeof value === 'string') {
            result[key] = value.length > MAX_STRING_LENGTH 
                ? value.substring(0, MAX_STRING_LENGTH) + '...[TRUNCATED]' 
                : value;
        } else if (isPlainObject(value)) {
            result[key] = sanitizeMetadata(value as Record<string, unknown>, currentDepth + 1);
        } else if (Array.isArray(value)) {
        // Replace arrays with a simple string representation to avoid deep/complex structures
            result[key] = `[Array(${value.length})]`;
        } else if (typeof value === 'function' || typeof value === 'symbol') {
            // Drop functions and symbols entirely
            continue;
        } else {
            // Primitives (number, boolean, null, undefined)
            result[key] = value;
        }
  }

    // Return undefined if the object is empty after processing
    return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Safely strips query parameters from a URL to avoid leaking PII via query strings.
 */
export function stripUrlQuery(urlStr?: string): string | undefined {
    if (!urlStr) return undefined;
    try {
        const url = new URL(urlStr);
        return `${url.origin}${url.pathname}`;
    } catch {
        // Fallback if URL parsing fails (e.g., relative path)
        return urlStr.split('?')[0];
    }
}
